import React, { useState } from 'react';
import { Box, Typography, Chip, Collapse, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import type { TransactionAlert } from '../TransactionAlertsSection';

/**
 * One card per monitoring RULE (not per alert, and not per rule type — two rules
 * of the same type stay separate). Collapsed it shows the rule's latest alert;
 * the caret reveals the earlier ones, newest first, paging further back on demand.
 *
 * Grouping is per rule because a process can run several rules of one type
 * ("Stopped near depot" and "Idle driver check" are both DRIVER_STOPPED) and
 * merging them would interleave unrelated messages under one heading.
 */

export interface AlertGroup {
  configUid: string;
  ruleName: string;
  ruleType: string;
  /** Alerts already loaded, newest first. */
  items: TransactionAlert[];
  /** True count from the DB — may exceed items.length, which is capped by the list endpoint. */
  total: number;
  /** Worst severity across the group, so one CRITICAL is never hidden by newer WARNINGs. */
  worst: 'INFO' | 'WARNING' | 'CRITICAL';
}

interface Props {
  group: AlertGroup;
  ruleTypeLabel: (ruleType: string) => string;
  severityColor: (severity: string) => any;
  substituteStepUids: (text: string, ctx: any) => string;
  formatRelativeTime: (iso: string) => string;
  /**
   * Fetch this rule's alerts past the ones already held. `skip` is how many of
   * this rule's alerts the card already has — not a page number, because the
   * capped list response supplies an arbitrary starting count that no fixed page
   * size lines up with.
   */
  onLoadOlder?: (configUid: string, skip: number, limit: number) => Promise<TransactionAlert[]>;
}

// How many history rows to show before "Load older" appears. Keeps one chatty
// rule from recreating the endless scroll inside its own card.
const PAGE_SIZE = 10;

const AlertGroupCard: React.FC<Props> = ({
  group,
  ruleTypeLabel,
  severityColor,
  substituteStepUids,
  formatRelativeTime,
  onLoadOlder
}) => {
  const [open, setOpen] = useState(false);
  const [extra, setExtra] = useState<TransactionAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);

  const colors = severityColor(group.worst);
  const latest = group.items[0];
  if (!latest) return null;

  // The latest lives in the header, so the history lists only what came before it.
  const earlier = [...group.items.slice(1), ...extra];
  const visible = earlier.slice(0, PAGE_SIZE + extra.length);
  // `total` counts every alert including the latest; earlier is total - 1.
  const earlierTotal = Math.max(group.total - 1, 0);
  const hasMore = !exhausted && visible.length < earlierTotal;

  const handleLoadOlder = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onLoadOlder || loading) return;
    setLoading(true);
    try {
      // Skip exactly what this card already holds for the rule, so the next batch
      // continues where the rendered history ends.
      const loaded = group.items.length + extra.length;
      const rows = await onLoadOlder(group.configUid, loaded, PAGE_SIZE);
      if (!rows.length) {
        // Nothing came back — stop offering the button rather than looping on it.
        setExhausted(true);
      } else {
        // Belt-and-braces de-dupe: a concurrent fire can shift the window by one
        // between the skip being computed and the query running.
        const seen = new Set([...group.items, ...extra].map((a) => a.uid).filter(Boolean));
        const fresh = rows.filter((r) => !r.uid || !seen.has(r.uid));
        if (!fresh.length) setExhausted(true);
        else setExtra((prev) => [...prev, ...fresh]);
      }
    } catch {
      setExhausted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={(theme) => ({
        bgcolor: (theme.palette as any)[colors.bg.split('.')[0]]?.lighter || colors.bgFallback,
        borderLeft: '4px solid',
        borderColor: colors.border,
        borderRadius: 1,
        px: 1.5,
        py: 1
      })}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, cursor: 'pointer', userSelect: 'none' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {group.ruleName || ruleTypeLabel(group.ruleType)}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={ruleTypeLabel(group.ruleType)}
              sx={{
                fontSize: '0.65rem',
                height: 18,
                color: colors.chipText,
                borderColor: colors.chipBorder,
                bgcolor: 'rgba(255,255,255,0.6)',
                fontWeight: 600
              }}
            />
            <Chip
              size="small"
              label={group.worst}
              sx={{
                fontSize: '0.65rem',
                height: 18,
                bgcolor: colors.pill,
                color: '#fff',
                fontWeight: 700,
                letterSpacing: 0.3
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {substituteStepUids(latest.message, latest.context)}
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title={new Date(latest.firedAt).toLocaleString()}>
              <Typography variant="caption" color="text.secondary">
                {formatRelativeTime(latest.firedAt)}
              </Typography>
            </Tooltip>
            <Chip
              size="small"
              label={`${group.total} alert${group.total === 1 ? '' : 's'}`}
              sx={{
                fontSize: '0.65rem',
                height: 18,
                fontWeight: 700,
                bgcolor: 'rgba(255,255,255,0.75)',
                border: '1px solid rgba(0,0,0,0.09)'
              }}
            />
          </Box>
        </Box>
        {earlierTotal > 0 && (
          <IconButton size="small" sx={{ mt: -0.25 }} aria-label={open ? 'Hide earlier alerts' : 'Show earlier alerts'}>
            <ExpandMore
              fontSize="small"
              sx={{ transition: 'transform .18s', transform: open ? 'rotate(180deg)' : 'none' }}
            />
          </IconButton>
        )}
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px dashed rgba(0,0,0,0.16)' }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
          >
            Earlier · {earlierTotal}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {visible.map((a, idx) => (
              <Box
                key={a.uid || idx}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  py: 0.6,
                  borderBottom: idx === visible.length - 1 ? 0 : '1px solid rgba(0,0,0,0.055)'
                }}
              >
                <Tooltip title={new Date(a.firedAt).toLocaleString()}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flex: '0 0 52px', fontVariantNumeric: 'tabular-nums', pt: '1px' }}
                  >
                    {formatRelativeTime(a.firedAt)}
                  </Typography>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                  {substituteStepUids(a.message, a.context)}
                </Typography>
              </Box>
            ))}
          </Box>

          {hasMore && (
            <Box
              sx={{
                mt: 0.75,
                px: 1,
                py: 0.6,
                borderRadius: 0.75,
                bgcolor: 'rgba(255,255,255,0.6)',
                border: '1px solid rgba(0,0,0,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Showing {visible.length} of {earlierTotal} earlier
              </Typography>
              {onLoadOlder &&
                (loading ? (
                  <CircularProgress size={13} />
                ) : (
                  <Typography
                    variant="caption"
                    onClick={handleLoadOlder}
                    sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Load older
                  </Typography>
                ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AlertGroupCard;
